<template>
  <BaseWidget
    :class="['payments-overview-widget', widget.type]"
    :loading="loading" 
    :widget-options="widgetMenuOptions" 
    @refresh-data="refreshData" 
    @set-config="setConfig"
    v-on="$listeners"
  >
    <template v-slot:header>
      <div class="title">
        <span class="title-icon">
        </span>
        {{ title }}
      </div>
      <div class="description">{{ filterText }}</div>
    </template>
    <template v-slot:content>
      <div v-if="config.dir === 'in' || config.dir === 'all'" class="payment-list">
        <div class="payment-list-toggle payment-item" v-b-toggle="'payments-in-collapse-' + widget.id">
          <div class="label"><font-awesome-icon icon="fa-solid fa-square-plus" /><font-awesome-icon icon="fa-solid fa-square-minus" /> {{ $gettext('Payments in') }}</div>
          <div class="amount text-brand">€{{ $options.filters.formatNumber(incomingTotal) }}</div>
        </div>
        <b-collapse visible :id="'payments-in-collapse-' + widget.id">
          <div class="payment-sublist">
            <div class="payment-list-toggle payment-item" v-b-toggle="'payments-in-collapse-sent-' + widget.id">
              <div class="label"><font-awesome-icon icon="fa-solid fa-square-plus" /><font-awesome-icon icon="fa-solid fa-square-minus" /> {{ sentPayments.length }} {{ $gettext('sent invoices') }}</div>
              <div class="amount">€{{ $options.filters.formatNumber(sentPaymentsTotal) }}</div>
            </div>
            <b-collapse visible :id="'payments-in-collapse-sent-' + widget.id">
              <a :href="viewInvoiceUrl(item.id)" v-for="(item, index) in sentPayments" :key="'in-sent-' + index" class="payment-item text-appinv">
                <div>{{ item.number }}</div>
                <div>{{ $options.filters.formatNumber(item.amount) }}</div>
              </a>
            </b-collapse>
          </div>

          <div class="payment-sublist">
            <div class="payment-list-toggle payment-item" v-b-toggle="'payments-in-collapse-overdue-' + widget.id">
              <div class="label"><font-awesome-icon icon="fa-solid fa-square-plus" /><font-awesome-icon icon="fa-solid fa-square-minus" /> {{ overduePayments.length }} {{ $gettext('overdue invoices') }}</div>
              <div class="amount">€{{ $options.filters.formatNumber(overduePaymentsTotal) }}</div>
            </div>
            <b-collapse visible :id="'payments-in-collapse-overdue-' + widget.id">
              <a :href="viewInvoiceUrl(item.id)" v-for="(item, index) in overduePayments" :key="'in-overdue-' + index" class="payment-item text-appinv">
                <div>{{ item.number }}</div>
                <div>€{{ $options.filters.formatNumber(item.amount) }}</div>
              </a>
            </b-collapse>
          </div>
        </b-collapse>
      </div>

      <div v-if="config.dir === 'out' || config.dir === 'all'" class="payment-list">
        <div class="payment-list-toggle payment-item" v-b-toggle="'payments-out-collapse-' + widget.id">
          <div class="label"><font-awesome-icon icon="fa-solid fa-square-plus" /><font-awesome-icon icon="fa-solid fa-square-minus" /> {{ $gettext('Payments out') }}</div>
          <div class="amount text-brown">€{{ $options.filters.formatNumber(outgoingTotal) }}</div>
        </div>
        <b-collapse visible :id="'payments-out-collapse-' + widget.id">
          <div class="payment-sublist">
            <div class="payment-list-toggle payment-item text-brown" v-b-toggle="'payments-out-collapse-unpaid-' + widget.id">
              <div class="label"><font-awesome-icon icon="fa-solid fa-square-plus" /><font-awesome-icon icon="fa-solid fa-square-minus" /> {{ outgoingCosts.length }} {{ $gettext('unpaid costs') }}</div>
              <div class="amount">€{{ $options.filters.formatNumber(outgoingTotal) }}</div>
            </div>
            <b-collapse visible :id="'payments-out-collapse-unpaid-' + widget.id">
              <a href="#" @click.prevent="openCostForEdit(item.id)" v-for="(item, index) in outgoingCosts" :key="'out-cost-' + index" class="payment-item text-appinv">
                <div>{{ item.number }}</div>
                <div>€{{ $options.filters.formatNumber(item.amount) }}</div>
              </a>
            </b-collapse>
          </div>
        </b-collapse>
      </div>
    </template>
  </BaseWidget>
</template>

<script>
import { mapGetters } from 'vuex';

import BaseWidget from "@/components/dashboard/widgets/BaseWidget"
import DashboardService from "@/store/services/dashboard.service.js";
export default {
  name: 'payments-overview-widget',
  components: { BaseWidget },
  props: {
    widget: {
      type: Object,
      default: () => {}
    },
  },
  data() {
    return {
      loading: true,
      title: '',
      comingUpData: {},
      config: { dir: 'all' },
      filters: { }
    }
  },
  computed: {
    ...mapGetters({ 
      getCompanyId: 'main/getCompanyId'
    }),
    widgetMenuOptions () {
      if (this.loading) return []
      return [
        { name: 'All payments', event: { name: 'set-config', config: { dir: 'all' } } },
        { name: 'Invoices', event: { name: 'set-config', config: { dir: 'in' } } },
        { name: 'Costs', event: { name: 'set-config', config: { dir: 'out' } } }
      ]
    },
    filterText () {
      let text = this.$gettext('Invoices and Costs that still require payment')
      if (this.config.dir === 'in') text = this.$gettext('Invoices that still require payment')
      if (this.config.dir === 'out') text = this.$gettext('Costs that still require payment')
      return text
    },
    sentPayments () {
      if (this.loading) return []
      return this.comingUpData.sent
    },
    overduePayments () {
      if (this.loading) return []
      return this.comingUpData.overdue
    },
    outgoingCosts () {
      if (this.loading) return []
      return this.comingUpData.costs
    },
    sentPaymentsTotal () {
      if (this.loading) return ''
      return this.sentPayments.map(item => item.amount).reduce((prev, next) => prev + next, 0);
    },
    overduePaymentsTotal () {
      if (this.loading) return ''
      return this.overduePayments.map(item => item.amount).reduce((prev, next) => prev + next, 0);
    },
    outgoingTotal () {
      if (this.loading) return ''
      return this.outgoingCosts.map(item => item.amount).reduce((prev, next) => prev + next, 0);
    },
    incomingTotal () {
      if (this.loading) return ''
      return this.sentPaymentsTotal + this.overduePaymentsTotal
    },
  },
  mounted () {
    this.setupWidget()
    this.setObjectDir(this.widget.config)
  },
  methods: {
    setConfig (config) {
      if (config.dir) this.setObjectDir(config)
      this.$emit('save-config', this.config)
    },
    setObjectDir (config) {
      if (config.dir) {
        this.config.dir = config.dir
      } 
      this.setFilters()
    },
    setFilters (dir = false) {
      this.filters.dir = (dir) ? dir : this.config.dir
      this.refreshData()
    },
    refreshData () {
      this.getData()
    },
    getData () {
      this.loading = true
      // COMING UP
      DashboardService.getComingUp({ sender_company: this.getCompanyId, company_profile: this.getCompanyId }).then((response) => {
        this.comingUpData = response.data
        this.loading = false
      })
    },
    setupWidget () {
      this.title = this.$gettext('To be paid')
    },
    viewInvoiceUrl (invoice_id) {
      return window.Urls['invoices_view_invoice'](invoice_id)
    },
    openCostForEdit (cost_id) {
      this.$router.push({name: 'EditCost', params: {costId: cost_id}});
    }
  }
}
</script>

<style lang="scss">
.payments-overview-widget {
  &.widget-wrapper {
    .content {
      flex-direction: column;
    }
  }
  .payment-list {
    > * { line-height: 1; }
    padding: 7px;
    border-radius: 8px;
    margin: -7px;
    flex: 0;
    &:not(:last-child) {
      margin-bottom: 15px;
    }
    .payment-list-toggle {
      padding: 5px 0px;
      font-size: 20px;
      color: $colorTextBase;
      .svg-inline--fa {
        opacity: 0.5;
        font-size: 16px;
        color: $colorBlue;
      }
      &.collapsed {
        .fa-square-minus { display: none; }
        .fa-square-plus { display: inline; }
      }
      &.not-collapsed {
        .fa-square-plus { display: none; }
        .fa-square-minus { display: inline; }
      }
    }
    .payment-sublist {
      padding-left: 10px;
      .payment-list-toggle {
        font-size: 16px !important;
        color: $colorBrand;
        .svg-inline--fa { font-size: 12px; }
      }
      .collapse {
        padding-left: 10px;
        margin-left: 4px;
        border-left: 2px solid #e5e7eb;
      }
      .payment-item:not(.payment-list-toggle) {
        font-size: 14px;
        padding: 5px 0px;
        &:hover {
          color: $colorBlue800;
          text-decoration: none;
        }
      }
    }
  }
  .payment-item {
    width: 100%;
    display: flex;
    justify-content: space-between;
    .label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .amount {
      white-space: nowrap;
    }
  }
}
</style>
